
  import { Component, Vue } from 'vue-property-decorator'
  import axios from 'axios'
  import { sendError } from '@/utils/notificationError'
  import { updateJWT } from '@/store/resources/form/actions'
  import { removeAccents } from '@/utils/general'
  import { mapGetters } from 'vuex'

  interface Task {
    id: string
    name: string
    status: string
    description: string
    url: string
  }

  @Component({
    computed: {
      ...mapGetters('user', ['user']),
    },
  })
  export default class ListTickets extends Vue {
    private tasks: Task[] = []
    private filteredTasks: Task[] = []
    private loading = true
    private error = ''
    private activeTab = 0
    private taskDialog = false
    private selectedTask: Task | null = null
    private searchQuery = ''
    private showSnackbar = false
    private errorSnackbarVisible = false
    private errorMessage = ''
    private loadingProgress = 0
    private loadingStates = {
      pendiente: true,
      'por hacer': true,
      'en progreso': true,
      cerrada: true,
    }

    private user!: { email: string }

    // Diccionario de roles
    private rolesDictionary: Record<string, string> = {
      staff: 'Ejecutivo',
      forwarder: 'Derivador',
      appraiser: 'Tasador',
      supervisor: 'Supervisor',
      inspector: 'Inspector',
      validator: 'Validador',
      advertiser: 'Anunciante',
      financer: 'Ejecutivo de financiamiento',
      treasurer: 'Tesorero',
      sysop: 'Administrador',
    }

    // Diccionario de secciones específicas
    private sectionsDictionary: Record<string, string> = {
      appraisals: 'Tasaciones',
      appraisal: 'Tasaciones',
      leads: 'Leads',
      employee: 'Empleados',
      clients: 'Clientes',
      auto: 'Vehículos',
      inventory: 'Inventario',
      users: 'Usuarios',
      financing: 'Financiamientos',
      settings: 'Configuración',
      inspection: 'Inspecciones',
      inspections: 'Inspecciones',
      negotiation: 'Negociaciones',
      negotiations: 'Negociaciones',
      evaluation: 'Evaluaciones',
      evaluations: 'Evaluaciones',
      publish_stock: 'Publicación de stock',
      enablement_tasks: 'Tareas de habilitación',
      stock_documentation: 'Documentación de stock',
      transfer_stock: 'Traspasos',
      transfer: 'Traspasos',
      payment_orders: 'Órdenes de pago',
      account_balance: 'Saldo bancario',
      account: 'Saldo bancario',
      income: 'Abonos',
      expense: 'Gastos',
      sales: 'Ventas',
      expenses: 'Gastos',
      purchases: 'Compras',
      consignments: 'Consignaciones',
      consignment: 'Consignaciones',
      purchase: 'Compras',
      enablement: 'Habilitación',
      stock: 'Stock',
      reservations: 'Reservas',
      reserve: 'Reservas',
      payment: 'Pagos',
      sale: 'Ventas',
      balance: 'Balance',
      reports: 'Reportes',
      dashboard: 'Dashboard',
      admin: 'Administración',
      position: 'Cargos',
      config: 'Reglas de negocio',
      message: 'Mensajería',
      web_config: 'Configuración web',
      delivery: 'Entregas',
      team: 'Equipo',
      sales_purchases: 'Compras y ventas',
      all: 'Todos',
    }

    get groupedTasks (): Record<string, Task[]> {
      // Inicializar con estados predefinidos
      const grouped: Record<string, Task[]> = {
        pendiente: [],
        'por hacer': [],
        'en progreso': [],
        cerrada: [],
      }

      // Agrupar las tareas existentes
      this.tasks.forEach(task => {
        const status = task.status.toLowerCase()
        if (!grouped[status]) {
          grouped[status] = []
        }
        grouped[status].push(task)
      })

      return grouped
    }

    get filteredGroupedTasks (): Record<string, Task[]> {
      // Inicializar con estados predefinidos
      const filtered: Record<string, Task[]> = {
        pendiente: [],
        'por hacer': [],
        'en progreso': [],
        cerrada: [],
      }

      // Agrupar las tareas filtradas por estado
      this.filteredTasks.forEach(task => {
        const status = task.status.toLowerCase()
        if (!filtered[status]) {
          filtered[status] = []
        }
        filtered[status].push(task)
      })

      return filtered
    }

    get orderedTabStatuses (): string[] {
      // Definir estados predefinidos
      const defaultStatuses = ['pendiente', 'por hacer', 'en progreso', 'cerrada']

      // Si no hay tareas, devolver los estados predefinidos
      if (this.tasks.length === 0) {
        return defaultStatuses
      }

      // Si hay tareas, obtener los estados existentes
      const existingStatuses = Object.keys(this.groupedTasks)

      // Definir el orden de prioridad para las pestañas
      const priorityOrder: Record<string, number> = {
        pendiente: 1,
        'por hacer': 2,
        'en progreso': 3,
        cerrada: 4,
      }

      // Ordenar las pestañas según la prioridad definida
      return existingStatuses.sort((a, b) => {
        const priorityA = priorityOrder[a.toLowerCase()] || 99
        const priorityB = priorityOrder[b.toLowerCase()] || 99
        return priorityA - priorityB
      })
    }

    mounted () {
      this.fetchTasks()
    }

    private async loadTasksForStatus (status: string, token: string): Promise<void> {
      try {
        this.$set(this.loadingStates, status.toLowerCase(), true)
        const { data } = await axios.get(
          `${process.env.VUE_APP_BASE_URL}/clickup-integration/lists/${process.env.VUE_APP_LIST_ID}/tasks`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            params: {
              status,
            },
          }
        )

        const tasksForStatus = data
          .map((task: any) => ({
            id: task.id,
            name: task.name,
            status: typeof task.status === 'string' ? task.status : task.status?.name || 'Sin estado',
            description: task.description,
            url: task.url,
          }))
          .filter((task: Task) => {
            const taskUser = this.getTaskUser(task)
            return taskUser === this.user.email
          })

        // Actualizamos el array principal de tareas
        const newTasks = [...this.tasks, ...tasksForStatus]
        this.$set(this, 'tasks', newTasks)

        // Actualizamos la lista filtrada si es necesario
        if (!this.searchQuery || this.searchQuery.trim() === '') {
          this.$set(this, 'filteredTasks', newTasks)
        } else {
          this.filterTasks()
        }

        this.$set(this.loadingStates, status.toLowerCase(), false)

        // Forzamos la actualización de la vista inmediatamente
        this.$forceUpdate()

        // Usamos $nextTick para asegurar que los cambios se apliquen
        await this.$nextTick()
      } catch (error) {
        console.error(`Error al cargar tareas con estado ${status}:`, error)
        this.$set(this.loadingStates, status.toLowerCase(), false)
        this.handleError(error, `Error al cargar tareas con estado ${status}`)
      }
    }

    private async loadStates (statuses: string[], token: string): Promise<void> {
      // Primero cargamos "pendiente" para mostrar resultados rápidamente
      if (statuses.includes('pendiente')) {
        await this.loadTasksForStatus('pendiente', token)
        this.loadingProgress = 25

        // Permitir que el DOM se actualice antes de continuar
        await new Promise(resolve => setTimeout(resolve, 50))
      }

      // Luego cargamos el resto de estados con un pequeño retraso entre ellos
      for (const status of statuses) {
        if (status !== 'pendiente') { // Saltamos pendiente que ya lo cargamos
          await this.loadTasksForStatus(status, token)
          this.loadingProgress = Math.min(100, this.loadingProgress + (75 / (statuses.length - 1)))

          // Permitir que el DOM se actualice antes de continuar con el siguiente estado
          await new Promise(resolve => setTimeout(resolve, 50))
        }
      }
    }

    async fetchTasks (): Promise<void> {
      const statuses = ['pendiente', 'por hacer', 'en progreso', 'cerrada']

      this.loading = true
      this.error = ''
      this.loadingProgress = 0
      this.tasks = []
      this.filteredTasks = []

      // Reiniciar estados de carga
      statuses.forEach(status => {
        this.$set(this.loadingStates, status, true)
      })

      await updateJWT()
      const token = localStorage.getItem('apollo-token')

      // Establecer la pestaña activa en "pendiente" (índice 0) por defecto
      this.activeTab = 0

      try {
        await this.loadStates(statuses, token)
        this.loading = false
        this.loadingProgress = 100
      } catch (error) {
        console.error('Error al cargar tareas:', error)
        this.handleError(error, 'Error general al cargar las tareas')
        this.loading = false
        this.loadingProgress = 0
        // Asegurarse de que todos los estados de carga se desactiven en caso de error
        statuses.forEach(status => {
          this.$set(this.loadingStates, status, false)
        })
      }
    }

    filterTasks () {
      // Comprobar si searchQuery es null, undefined o vacío
      if (!this.searchQuery || this.searchQuery.trim() === '') {
        this.$set(this, 'filteredTasks', [...this.tasks])
        return
      }

      const query = removeAccents(this.searchQuery.toLowerCase().trim())
      const filtered = this.tasks.filter(task => {
        // Buscar en ID
        if (removeAccents(task.id.toLowerCase()).includes(query)) {
          return true
        }

        // Buscar en título
        const taskTitle = removeAccents(this.getTaskTitle(task).toLowerCase())
        if (taskTitle.includes(query)) {
          return true
        }

        // Buscar en rol
        const taskRole = this.getTaskRole(task)
        if (taskRole && removeAccents(taskRole.toLowerCase()).includes(query)) {
          return true
        }

        // Buscar en sección
        const taskSection = this.getTaskSection(task)
        if (taskSection && removeAccents(taskSection.toLowerCase()).includes(query)) {
          return true
        }

        // Buscar en solicitante
        const taskUser = this.getTaskUser(task)
        if (taskUser && removeAccents(taskUser.toLowerCase()).includes(query)) {
          return true
        }

        return false
      })

      this.$set(this, 'filteredTasks', filtered)
    }

    private handleError (error: any, message?: string): void {
      this.error = 'Error al cargar los requerimientos. Por favor, intente nuevamente.'
      const context = {
        app_name: 'GENIO',
        view: this.$router.currentRoute.path,
        record_id: 'list-tickets',
        context: `${JSON.stringify(error)} ${message || 'error obteniendo tickets'}`,
      }
      sendError(context)

      // Mostrar mensaje genérico indicando contactar a soporte técnico
      this.displayErrorSnackbar('Error al cargar los requerimientos. Por favor, contacta a soporte técnico.')
    }

    // Añadir un nuevo método para mostrar snackbar de error
    private displayErrorSnackbar (message: string): void {
      this.errorMessage = message
      this.errorSnackbarVisible = true
    }

    formatDate (dateString: string | null): string {
      if (!dateString) return ''

      const date = new Date(dateString)
      return date.toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    }

    getStatusColor (status: string): string {
      const statusColors: Record<string, string> = {
        pendiente: 'grey',
        'por hacer': 'orange',
        'en progreso': 'blue',
        cerrada: 'green',
      }

      return statusColors[status.toLowerCase()] || 'primary'
    }

    transformStatus (status: string): string {
      if (status.toLowerCase() === 'pendiente') {
        return 'Pendiente'
      }
      if (status.toLowerCase() === 'por hacer') {
        return 'Por hacer'
      }
      if (status.toLowerCase() === 'en progreso') {
        return 'En progreso'
      }
      return status.toLowerCase() === 'cerrada' ? 'Resuelto' : status
    }

    getTaskTitle (task: Task): string {
      // Si la descripción contiene "Comentario: ", extrae ese texto como título
      if (task.description && task.description.includes('Comentario: ')) {
        const match = task.description.match(/Comentario: (.+?)($|\n)/)
        if (match && match[1]) {
          return match[1]
        }
      }

      // Si el nombre tiene emoji, lo usa como está
      if (task.name.includes('✨') || task.name.includes('🐛')) {
        return task.name
      }

      // Usa la descripción completa como título de respaldo
      if (task.description) {
        // Limita la descripción a 100 caracteres y añade "..." si es más larga
        return task.description.length > 100
          ? `${task.description.substring(0, 100)}...`
          : task.description
      }

      // Si todo lo demás falla, usa el nombre
      return task.name
    }

    getTaskRole (task: Task): string | null {
      if (!task.name) return null

      let rolSlug: string | null = null

      // Caso dashboard: "/dashboard/supervisor_dashboard/"
      const dashboardPattern = /\/dashboard\/([^_]+)_dashboard/
      const dashboardMatch = task.name.match(dashboardPattern)
      if (dashboardMatch && dashboardMatch[1]) {
        rolSlug = dashboardMatch[1]
      } else if (task.name.includes('/schedule/')) { // Caso schedule: "/schedule/supervisor/index/delivery"
        const schedulePattern = /\/schedule\/([^/]+)\//
        const scheduleMatch = task.name.match(schedulePattern)
        if (scheduleMatch && scheduleMatch[1] && scheduleMatch[1] !== 'index') {
          rolSlug = scheduleMatch[1]
        }
      } else { // Caso datatable: "/datatable/supervisor/supervisor_appraisals"
        const datatablePattern = /\/datatable\/([^/]+)\//
        const datatableMatch = task.name.match(datatablePattern)
        if (datatableMatch && datatableMatch[1]) {
          rolSlug = datatableMatch[1]
        } else {
          // Caso form: "form/staff_leads"
          const formPattern = /\/form\/([^_]+)_/
          const formMatch = task.name.match(formPattern)
          if (formMatch && formMatch[1]) {
            rolSlug = formMatch[1]
          }
        }
      }

      // Traducir el slug al nombre completo del rol
      if (rolSlug && this.rolesDictionary[rolSlug]) {
        return this.rolesDictionary[rolSlug]
      }

      return null
    }

    getTaskSection (task: Task): string | null {
      if (!task.name) return null

      // Limpiamos la cadena quitando "identifier" o "user" y todo lo que viene después
      let cleanName = task.name
      const patterns = [' identifier-', ' user-']
      for (const pattern of patterns) {
        if (cleanName.includes(pattern)) {
          cleanName = cleanName.split(pattern)[0]
        }
      }

      // Eliminar cualquier dirección de correo electrónico en el nombre
      const emailPattern = /\s+[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}\b/g
      cleanName = cleanName.replace(emailPattern, '')

      // Procesar la sección
      return this.processSectionName(cleanName)
    }

    private processSectionName (cleanName: string): string | null {
      // Eliminar cualquier dirección de correo electrónico restante
      cleanName = cleanName.replace(/\b[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}\b/g, '').trim()

      let sectionType: string | null = null
      let sectionName: string | null = null

      if (cleanName.includes('/schedule/')) {
        // Caso schedule (agenda): "/schedule/index/delivery"
        sectionType = 'Agenda'
        sectionName = this.extractLastSegment(cleanName)
      } else if (cleanName.includes('/dashboard/')) {
        // Caso dashboard (KPI): "/dashboard/staff_dashboard/staff_sales_purchases"
        sectionType = 'KPI'
        sectionName = this.extractDashboardSection(cleanName)
      } else if (cleanName.includes('/datatable/')) {
        // Caso datatable: "/datatable/supervisor/supervisor_appraisals"
        sectionType = 'Datatable'
        sectionName = this.extractSectionAfterUnderscore(cleanName)
      } else if (cleanName.includes('/form/')) {
        // Caso form: "form/staff_leads"
        sectionType = 'Formulario'
        sectionName = this.extractSectionAfterUnderscore(cleanName)
      }

      // Formar la sección completa
      if (sectionType && sectionName) {
        return `${sectionType} ${sectionName}`
      }

      return null
    }

    // Extrae la sección después del último slash y traduce
    private extractLastSegment (path: string): string | null {
      const parts = path.split('/')
      const lastPart = parts[parts.length - 1]

      if (!lastPart) return null

      return this.translateSection(lastPart)
    }

    // Extrae sección específica del dashboard
    private extractDashboardSection (path: string): string | null {
      const parts = path.split('/')
      const lastPart = parts[parts.length - 1]

      if (!lastPart || !lastPart.includes('_')) return null

      // Tomar lo que viene después del primer guión bajo
      const sectionParts = lastPart.split('_')
      if (sectionParts.length <= 1) return null

      // Si hay varios guiones bajos juntos, tomar la parte final
      const sectionKey = sectionParts.slice(1).join('_')

      return this.translateSection(sectionKey)
    }

    // Extrae la sección después del guión bajo
    private extractSectionAfterUnderscore (path: string): string | null {
      const sectionPattern = /_([^_/]+)/
      const sectionMatch = path.match(sectionPattern)

      if (!sectionMatch || !sectionMatch[1]) return null

      return this.translateSection(sectionMatch[1])
    }

    // Traduce una sección usando el diccionario o capitaliza la primera letra
    private translateSection (section: string): string {
      if (this.sectionsDictionary[section]) {
        return this.sectionsDictionary[section]
      }

      // Si no está en el diccionario, capitalizar y reemplazar guiones bajos por espacios
      return section.charAt(0).toUpperCase() + section.slice(1).replace(/_/g, ' ')
    }

    getTaskUser (task: Task): string | null {
      if (!task.name) return null

      // Buscar correo después de un guión: "identifier-11-rafael.torrealba@automotrizpetersen.cl"
      const emailPattern = /-([^-\s]+@[^-\s]+(?:\.[^-\s]+)+)/
      const match = task.name.match(emailPattern)
      if (match && match[1]) {
        return match[1]
      }

      // Buscar un correo electrónico en cualquier parte del nombre
      // Esto capturará casos como "Datatable Leads admin@example.com"
      const simpleEmailPattern = /\b([a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})\b/
      const simpleMatch = task.name.match(simpleEmailPattern)
      if (simpleMatch && simpleMatch[1]) {
        return simpleMatch[1]
      }

      // Si no se encuentra en el nombre, buscar en la descripción
      if (task.description) {
        // Buscar patrón "• Email: correo@ejemplo.com"
        const descEmailPattern = /• Email:\s*([^\s]+@[^\s]+(?:\.[^\s]+)+)/
        const descMatch = task.description.match(descEmailPattern)
        if (descMatch && descMatch[1]) {
          return descMatch[1]
        }

        // Buscar un correo electrónico en cualquier parte de la descripción
        const descSimpleMatch = task.description.match(simpleEmailPattern)
        if (descSimpleMatch && descSimpleMatch[1]) {
          return descSimpleMatch[1]
        }
      }

      return null
    }

    formatDescription (description: string): string {
      if (!description) return ''

      // Extraer el comentario si existe
      if (description.includes('Comentario: ')) {
        const match = description.match(/Comentario: (.+?)($|$)/s)
        if (match && match[1]) {
          description = match[1].trim()
        }
      } else if (description.includes('• Email:') || description.includes('• URL:')) {
        // Eliminar la información de Email y URL si existe
        description = description
          .replace(/• Email:[^\n]+\n?/g, '')
          .replace(/• URL:[^\n]+\n?/g, '')
          .trim()
      }

      // Convierte URLs en enlaces clickeables
      const urlPattern = /(https?:\/\/[^\s]+)/g
      const textWithLinks = description.replace(urlPattern, '<a href="$1" target="_blank">$1</a>')

      // Convierte saltos de línea en <br>
      return textWithLinks.replace(/\n/g, '<br>')
    }

    showTaskDetails (task: Task) {
      this.selectedTask = task
      this.taskDialog = true
      this.copyTaskId(task.id, false) // Copia el ID pero no muestra notificación
    }

    copyTaskId (id: string, showNotification = true) {
      navigator.clipboard.writeText(id).then(() => {
        if (showNotification) {
          this.showSnackbar = true
        }
      }).catch(err => {
        console.error('Error al copiar el ID:', err)
      })
    }
  }
